import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import { useMetaTags } from '../../hooks/useMetaTags';

function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, login } = useUser();
  
  // Get redirect URL from location state or default to /settings
  const redirectTo = location.state?.from?.pathname || '/';
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  
  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Refs
  const emailInputRef = useRef<HTMLInputElement>(null);
  
  // Set meta tags
  useMetaTags(
    t('auth.login', 'Login'),
    t('auth.loginDescription', 'Login to access your account')
  );
  
  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn) {
      navigate(redirectTo);
    }
  }, [isLoggedIn, navigate, redirectTo]);
  
  // Focus email input on load
  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors
    setFormError('');
    
    if (!email || !password) {
      setFormError(t('auth.allFieldsRequired', 'Please fill in all fields'));
      return;
    }
    
    try {
      setIsSubmitting(true);
      const success = await login(email, password);
      
      if (success) {
        navigate(redirectTo);
      } else {
        setFormError(t('auth.invalidCredentials', 'Invalid email or password'));
      }
    } catch (error) {
      console.error('Login error:', error);
      setFormError(t('auth.loginFailed', 'Login failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-md-8 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h1 className="h4 mb-4 text-center">{t('auth.login', 'Login')}</h1>
              
              {formError && (
                <div className="alert alert-danger" role="alert">
                  {formError}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                {/* Email */}
                <div className="mb-3">
                  <label htmlFor="email" className="form-label">
                    {t('auth.email', 'Email')}
                  </label>
                  <input
                    ref={emailInputRef}
                    type="email"
                    className="form-control"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>
                
                {/* Password */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between">
                    <label htmlFor="password" className="form-label">
                      {t('auth.password', 'Password')}
                    </label>
                    <Link to="/forgot-password" className="text-primary small">
                      {t('auth.forgotPassword', 'Forgot Password?')}
                    </Link>
                  </div>
                  <input
                    type="password"
                    className="form-control"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.enterPassword', 'Enter your password')}
                    required
                    aria-describedby="passwordHelp"
                  />
                  <div id="passwordHelp" className="form-text">
                    {t('auth.passwordHelp', 'Enter the password associated with your account')}
                  </div>
                </div>
                
                {/* Submit Button */}
                <div className="d-grid mt-4">
                  <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('common.loading', 'Loading...')}
                      </>
                    ) : (
                      t('auth.login', 'Login')
                    )}
                  </button>
                </div>
              </form>
              
              {/* Signup Link */}
              <div className="mt-4 text-center">
                <span>{t('auth.noAccount', 'Don\'t have an account?')} </span>
                <Link to="/signup" className="text-primary">
                  {t('auth.signup', 'Sign up')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login; 